/**
 * Headless owner-side backend connection.
 *
 * Owns the single backend WebSocket and the REST frame load for the desktop
 * OWNER window — decoupled from the lazily-mounted <App/>. Without this, the
 * owner only connected after first entering peek/workspace/denk, so mirrored
 * monitors stayed blank until the user opened denk mode on the owner display.
 *
 * Mounted twice, run once:
 *   - DesktopShell mounts it with { enabled: true } (Tauri only; always present
 *     for every webview, so the owner connects at bootstrap regardless of mode).
 *   - WorkspacePage mounts it with { enabled: !isTauri() } so the WEB SPA — which
 *     has no DesktopShell — still gets exactly one connection.
 *
 * Mirrors construct no WS; they receive state via the mirror channel
 * (use-state-mirror.ts) and send actions via use-mirror-actions.ts shims.
 */
import { useCallback, useEffect, useRef } from "react";
import { createOwnerWorkReconciler } from "./owner-work-reconciliation";
import { useProjectStore } from "@/stores/project-store";
import { useCanvasStore } from "@/stores/canvas-store";
import { useWorkspaceLoadStore } from "@/stores/workspace-load-store";
import { useCompanionStore } from "@/stores/companion-store";
import { useConversationStore } from "@/stores/conversation-store";
import { useWorkToastStore } from "@/stores/work-toast-store";
import { useActivityStore } from "@/stores/activity-store";
import { useTasksStore } from "@/stores/tasks-store";
import { useNotificationStore } from "@/stores/notification-store";
import { translate } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { libNoticesMessages } from "@/i18n/messages/lib-notices";
import { DenkerWebSocket } from "@/api/websocket";
import type { WsMessage } from "@/api/websocket";
import { setCurrentDenkerWs } from "@/api/canvas-ws-ref";
import { applyAgentDesktopPinRequest } from "@/desktop/pin/desktop-pin-agent-request";
import { applyAgentNewChatRequest } from "@/desktop/agent-new-chat-request";
import { routeDesktopPinEvent } from "@/desktop/pin/desktop-pin-relay";
import { OwnerWsActions } from "@/api/ws-actions-owner";
import * as spacesApi from "@/api/spaces";
import { useMemoryStore } from "@/stores/memory-store";
import { useAgentStore } from "@/stores/agent-store";
import { usePointingStore } from "@/stores/pointing-store";
import { startFocusStateReports } from "@/lib/focus/focus-state-report";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { useTTS } from "@/desktop/use-tts";
import { applyTauriCompanionFrame } from "@/lib/apply-tauri-companion-frame";
import { isStaleWorkToastQuestionFrame } from "@/lib/work-toast-question-delivery";
import { useOwnerChatHistory } from "./owner-chat-history";
import {
  denkerCanvasKeep,
  denkerFrameKeep,
  denkerProjectIds,
  settleCurrentExecutionOnSwitch,
  useOwnerExtraProjects,
} from "./owner-extra-projects";
import { withMutationEmitSuppressed } from "@/lib/mirrored-store";
import { useWindowRole, getWindowRoleSync } from "@/desktop/use-window-role";
import {
  getMyWindowLabel,
  getCurrentOwnerEpoch,
} from "@/lib/owner-state-client";
import { emitMirrorEvent } from "@/lib/mirror-event-emitter";
import { isTauri } from "@/lib/is-tauri";
import {
  getDesktopChatAuthScope,
  isCurrentDesktopChatAuthScope,
  type DesktopChatAuthScope,
} from "./desktop-chat-auth-scope";
import {
  armWorkToastAutoOpen,
  resetWorkToastAutoOpen,
} from "@/lib/work-toast-live-events";
import { loadWorkspaceFrames } from "@/lib/workspace-frame-load";
import { useFramesStore } from "@/stores/frames-store";

const FRAME_LOAD_TIMEOUT_MS = 15_000;

export function useOwnerConnection({ enabled }: { enabled: boolean }): void {
  const role = useWindowRole();
  const currentProjectId = useProjectStore((s) => s.currentProject?.id);

  const setCompanionStatus = useCompanionStore((s) => s.setStatus);
  const setWsConnectionState = useCompanionStore((s) => s.setWsConnectionState);
  const setTriggerReconnect = useCompanionStore((s) => s.setTriggerReconnect);
  const setSendPermissionResolved = useCompanionStore(
    (s) => s.setSendPermissionResolved,
  );
  const setSendRuntimeRefresh = useCompanionStore(
    (s) => s.setSendRuntimeRefresh,
  );

  const loadConnections = useCanvasStore((s) => s.loadConnections);
  const clearCanvas = useCanvasStore((s) => s.clear);
  const clearAgentCursorsForSpace = useCanvasStore(
    (s) => s.clearAgentCursorsForSpace,
  );

  const addSystemToast = useNotificationStore((s) => s.addSystemToast);

  const setIsLoadingCanvas = useWorkspaceLoadStore((s) => s.setIsLoadingCanvas);
  const setFramesLoadedSpaceId = useWorkspaceLoadStore(
    (s) => s.setFramesLoadedSpaceId,
  );
  const setLoadedSpaceId = useWorkspaceLoadStore((s) => s.setLoadedSpaceId);

  const wsCleanupRef = useRef<DenkerWebSocket | null>(null);
  const canvasLoadSeqRef = useRef(0);
  const frameLoadControllerRef = useRef<AbortController | null>(null);
  const connectionsLoadControllerRef = useRef<AbortController | null>(null);
  const loadedProjectRef = useRef<string | null>(null);
  const workReconcilerRef = useRef<ReturnType<
    typeof createOwnerWorkReconciler
  > | null>(null);
  const cursorRemovalTimers = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map());

  // Handle incoming WebSocket messages — thin wrapper around the pure reducer.
  // After applying locally, the owner emits the frame to all mirrors via the
  // `denker://mirror-event` channel so they apply through the same reducer.
  const handleWsMessage = useCallback(
    (msg: WsMessage, sourceAuthScope: DesktopChatAuthScope | null) => {
      if (isTauri() && !isCurrentDesktopChatAuthScope(sourceAuthScope)) return;
      // Pins' and Denker's extra projects never reach the viewed wall.
      if (routeDesktopPinEvent(msg)) return;
      if (applyAgentDesktopPinRequest(msg)) return;
      if (applyAgentNewChatRequest(msg)) return;
      if (isStaleWorkToastQuestionFrame(msg)) return;
      workReconcilerRef.current?.observe(msg);
      withMutationEmitSuppressed(() => {
        applyTauriCompanionFrame(msg, {
          companionStore: useCompanionStore,
          canvasStore: useCanvasStore,
          projectStore: useProjectStore,
          activityStore: useActivityStore,
          notificationStore: useNotificationStore,
          tasksStore: useTasksStore,
          memoryStore: useMemoryStore,
          agentStore: useAgentStore,
          pointingStore: usePointingStore,
          ttsStore: useTTS,
          onboardingStore: useOnboardingStore,
          conversationStore: useConversationStore,
          workToastStore: useWorkToastStore,
          cursorRemovalTimers: cursorRemovalTimers.current,
          defer: (work, delayMs) => setTimeout(work, delayMs),
          ownsSideEffects: true,
          addSystemToast,
        });
      });
      if (isTauri() && getWindowRoleSync() === "owner") {
        emitMirrorEvent({
          kind: "frame",
          from: getMyWindowLabel(),
          ownerEpoch: getCurrentOwnerEpoch(),
          authScope: sourceAuthScope,
          frame: msg,
        });
      }
    },
    [addSystemToast],
  );

  const loadCanvasData = useCallback(async () => {
    if (
      !currentProjectId ||
      useProjectStore.getState().currentProject?.id !== currentProjectId
    )
      return;
    const loadSeq = ++canvasLoadSeqRef.current;
    frameLoadControllerRef.current?.abort();
    const frameLoadController = new AbortController();
    frameLoadControllerRef.current = frameLoadController;
    const frameLoadTimeout = setTimeout(
      () => frameLoadController.abort(),
      FRAME_LOAD_TIMEOUT_MS,
    );
    const sourceAuthScope = getDesktopChatAuthScope();
    const shouldApplyLoadedData = () => {
      const projectState = useProjectStore.getState();
      return (
        (!isTauri() || isCurrentDesktopChatAuthScope(sourceAuthScope)) &&
        loadSeq === canvasLoadSeqRef.current &&
        getWindowRoleSync() === "owner" &&
        projectState.currentProject?.id === currentProjectId
      );
    };
    setIsLoadingCanvas(true);
    setFramesLoadedSpaceId(null);
    setLoadedSpaceId(null);
    const loadProjectConnections = () => {
      if (!shouldApplyLoadedData()) return;
      connectionsLoadControllerRef.current?.abort();
      const controller = new AbortController();
      connectionsLoadControllerRef.current = controller;
      const timeout = setTimeout(
        () => controller.abort(),
        FRAME_LOAD_TIMEOUT_MS,
      );
      const edgesBeforeLoad = useCanvasStore.getState().edges;
      void spacesApi
        .listConnections(currentProjectId, {
          signal: controller.signal,
          silent: true,
        })
        .then((connections) => {
          if (
            shouldApplyLoadedData() &&
            useCanvasStore.getState().edges === edgesBeforeLoad
          ) {
            loadConnections(connections);
          }
          if (shouldApplyLoadedData()) {
            useNotificationStore
              .getState()
              .removeSystemToast(`connections-load-${currentProjectId}`);
          }
        })
        .catch(() => {
          if (!shouldApplyLoadedData()) return;
          addSystemToast(
            () => ({
              id: `connections-load-${currentProjectId}`,
              title: translate(
                libNoticesMessages,
                "projectConnectionsLoadFailed",
              ),
              level: "error",
              actionLabel: translate(commonMessages, "retry"),
              persistent: true,
            }),
            loadProjectConnections,
          );
        })
        .finally(() => {
          clearTimeout(timeout);
          if (connectionsLoadControllerRef.current === controller) {
            connectionsLoadControllerRef.current = null;
          }
        });
    };
    loadProjectConnections();
    try {
      await loadWorkspaceFrames(currentProjectId, {
        signal: frameLoadController.signal,
        shouldApply: shouldApplyLoadedData,
      });
      if (!shouldApplyLoadedData()) return;
      // The chat is not the project's (`owner-chat-history.ts`): frames are all.
      setFramesLoadedSpaceId(currentProjectId);
      setLoadedSpaceId(currentProjectId);
      setIsLoadingCanvas(false);
      useNotificationStore
        .getState()
        .removeSystemToast(`canvas-load-${currentProjectId}`);
    } catch {
      if (shouldApplyLoadedData()) {
        addSystemToast(
          () => ({
            id: `canvas-load-${currentProjectId}`,
            title: translate(libNoticesMessages, "projectFramesLoadFailed"),
            level: "error",
            actionLabel: translate(commonMessages, "retry"),
            persistent: true,
          }),
          () => void loadCanvasData(),
        );
      }
    } finally {
      clearTimeout(frameLoadTimeout);
      if (frameLoadControllerRef.current === frameLoadController)
        frameLoadControllerRef.current = null;
      if (shouldApplyLoadedData()) {
        setIsLoadingCanvas(false);
      }
    }
  }, [
    currentProjectId,
    loadConnections,
    addSystemToast,
    setIsLoadingCanvas,
    setFramesLoadedSpaceId,
    setLoadedSpaceId,
  ]);

  // Effect 1 — WS lifecycle. Creates DenkerWebSocket only when this webview
  // is the owner. Mirrors do not construct a WS.
  useEffect(() => {
    if (!enabled || role !== "owner") return;

    resetWorkToastAutoOpen(useWorkToastStore.getState().items);
    const connectionAuthScope = getDesktopChatAuthScope();
    let disposed = false;
    const reconciler = createOwnerWorkReconciler(
      () =>
        !disposed &&
        getWindowRoleSync() === "owner" &&
        (!isTauri() || isCurrentDesktopChatAuthScope(connectionAuthScope)),
    );
    workReconcilerRef.current = reconciler;
    const newWs = new DenkerWebSocket({
      onMessage: (msg) => handleWsMessage(msg, connectionAuthScope),
      onStatusChange: (status) => {
        if (isTauri() && !isCurrentDesktopChatAuthScope(connectionAuthScope)) {
          return;
        }
        setWsConnectionState(status);
        if (status === "disconnected" || status === "failed") {
          setCompanionStatus("disconnected");
        }
        if (status === "connected") {
          void reconciler.refresh(
            useProjectStore.getState().currentProject?.id,
          );
        }
      },
    });
    wsCleanupRef.current = newWs;

    // REST hydration is silent and races safely with live frames. Never make
    // socket startup depend on the latency or availability of a REST request.
    armWorkToastAutoOpen(useWorkToastStore.getState().items);

    const ownerActions = new OwnerWsActions(newWs);

    setTriggerReconnect(() => newWs.manualReconnect());
    setSendPermissionResolved((executionId: string, skip: boolean) => {
      newWs.send({
        type: "permission.directory_resolved",
        data: { execution_id: executionId, skip },
      });
    });
    setSendRuntimeRefresh((forceMetadata = true) => {
      newWs.send({
        type: "runtime.refresh",
        data: { force_metadata: forceMetadata },
      });
    });
    setCurrentDenkerWs(ownerActions);
    newWs.connect();
    const stopFocusStateReports = startFocusStateReports();

    return () => {
      disposed = true;
      stopFocusStateReports();
      reconciler.dispose();
      if (workReconcilerRef.current === reconciler)
        workReconcilerRef.current = null;
      void import("@/lib/agent-presentation-controller").then(
        ({ agentPresentationController }) =>
          agentPresentationController.cancelAll("app_window_closed"),
      );
      newWs.disconnect();
      wsCleanupRef.current = null;
      setTriggerReconnect(null);
      setSendPermissionResolved(null);
      setSendRuntimeRefresh(null);
      setCurrentDenkerWs(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, role]);

  // Effect 2 — project subscription + REST load. OWNER ONLY. This hook is
  // mounted on every webview (via DesktopShell), so it must be completely
  // inert on mirrors: a mirror running clearCanvas() here
  // would push those mutations UPSTREAM (mirrored-store.ts) and blank the
  // owner + every other monitor. Mirrors get state solely from
  // use-state-mirror.ts. The owner subscribes to the new project and loads REST
  // data; web is always owner so it runs the full path there too.
  useEffect(() => {
    if (!enabled || role !== "owner") return;
    const currentWs = wsCleanupRef.current;
    if (loadedProjectRef.current !== (currentProjectId ?? null)) {
      if (loadedProjectRef.current) {
        useNotificationStore
          .getState()
          .removeSystemToast(`canvas-load-${loadedProjectRef.current}`);
        useNotificationStore
          .getState()
          .removeSystemToast(`connections-load-${loadedProjectRef.current}`);
      }
      // Denker's chat, run and its projects' off-wall frames, cursors and
      // questions stay, with no project viewed too; an account change clears.
      clearCanvas(denkerCanvasKeep());
      useFramesStore.getState().clear(denkerFrameKeep());
      settleCurrentExecutionOnSwitch();
      loadedProjectRef.current = currentProjectId ?? null;
    }
    if (!currentProjectId) {
      setFramesLoadedSpaceId(null);
      setLoadedSpaceId(null);
      return;
    }

    if (currentWs) {
      currentWs.subscribeProject(currentProjectId);
    }

    void workReconcilerRef.current?.refresh(currentProjectId);
    void loadCanvasData();

    const subscribedProjectId = currentProjectId;
    const cursorRemovalTimersForCleanup = cursorRemovalTimers.current;

    return () => {
      canvasLoadSeqRef.current += 1;
      frameLoadControllerRef.current?.abort();
      connectionsLoadControllerRef.current?.abort();
      setIsLoadingCanvas(false);
      if (currentWs) {
        currentWs.unsubscribeProject(subscribedProjectId);
      }
      if (!denkerProjectIds().has(subscribedProjectId))
        clearAgentCursorsForSpace(subscribedProjectId);
      const keptCursors = denkerCanvasKeep().agentCursors;
      for (const [key, timer] of cursorRemovalTimersForCleanup) {
        if (keptCursors.has(key)) continue;
        clearTimeout(timer);
        cursorRemovalTimersForCleanup.delete(key);
      }
    };
  }, [
    enabled,
    currentProjectId,
    role,
    loadCanvasData,
    clearCanvas,
    clearAgentCursorsForSpace,
    setIsLoadingCanvas,
    setFramesLoadedSpaceId,
    setLoadedSpaceId,
  ]);
  useOwnerExtraProjects(enabled && role === "owner");
  useOwnerChatHistory(enabled && role === "owner");

  // Re-sync execution state when the tab becomes visible again. Catches
  // completion/failure events lost while backgrounded or for an unsubscribed
  // space.
  useEffect(() => {
    if (!enabled || role !== "owner") return;
    const handler = () => {
      if (document.visibilityState !== "visible") return;
      void workReconcilerRef.current?.refresh(
        useProjectStore.getState().currentProject?.id,
      );
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [enabled, role]);
}
